import { Tooltip } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { PermissionSetType, UserStatus } from 'api/models';
import { PartnerUser } from 'api/partner/types';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';
import { useAccountMetaQuery } from 'queries';
import { CellRendererParams } from './types';

interface Props extends CellRendererParams {
  onEdit: (user: PartnerUser) => void;
  onReactivate: (user: PartnerUser) => void;
  onDeactivate: (user: PartnerUser) => void;
  onResendInvite: (user: PartnerUser) => void;
  partnerAdmins: PartnerUser[];
  availableLicensesInAccount: number;
  availableLicensesForPartner: number;
  partnerActiveStatus: boolean;
}
export const MoreActionsRenderer: FC<Props> = ({
  data: user,
  onEdit,
  onDeactivate,
  onReactivate,
  onResendInvite,
  partnerAdmins,
  availableLicensesInAccount,
  availableLicensesForPartner,
  partnerActiveStatus,
}) => {
  const { data: accountMeta } = useAccountMetaQuery();
  const { user: currentUser } = useUser();
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isBillingEnabled = useBillingFeatureFlag();
  const { userStatus, userId } = user;
  const isInvited = userStatus === UserStatus.Invited;
  const isActive = userStatus === UserStatus.Active || isInvited;
  const isSameUser = currentUser.userId === userId;
  const isPartnerAdmin = useMemo(
    () => !!partnerAdmins?.find((admin) => admin.userId === userId),
    [partnerAdmins, userId],
  );
  const edit = useCallback(() => {
    onEdit(user);
  }, [onEdit, user]);
  const changeStatus = useCallback(() => {
    if (isActive) {
      onDeactivate(user);
    } else {
      onReactivate(user);
    }
  }, [isActive, onDeactivate, onReactivate, user]);
  const resendInvite = useCallback(() => {
    onResendInvite(user);
  }, [onResendInvite, user]);

  const disableDeactivate = isActive && isPartnerAdmin;
  const disableReactivate =
    !isActive &&
    (partnerActiveStatus !== true ||
      (isBillingEnabled &&
        (currentUserPermissionSetType === PermissionSetType.PartnerAdmin
          ? availableLicensesForPartner === 0 ||
            availableLicensesInAccount === 0
          : availableLicensesForPartner === 0)));
  const disableReason = disableDeactivate ? (
    <FormattedMessage id="partners.team_members.errors.admin_not_be_deactivate" />
  ) : disableReactivate ? (
    partnerActiveStatus !== true ? (
      <FormattedMessage id="partners.team_members.errors.reactivate.partner_inactive" />
    ) : currentUserPermissionSetType === PermissionSetType.PartnerAdmin ? (
      availableLicensesForPartner === 0 ? (
        <FormattedMessage
          id="partners.team_members.errors.reactivate.partner_admin.no_partner_licenses"
          values={{
            companyName: accountMeta?.company.companyName,
          }}
        />
      ) : availableLicensesInAccount === 0 ? (
        <FormattedMessage
          id="partners.team_members.errors.reactivate.partner_admin.no_account_licenses"
          values={{
            companyName: accountMeta?.company.companyName,
          }}
        />
      ) : null
    ) : (
      <FormattedMessage id="partners.team_members.errors.reactivate.vendor_admin.no_partner_licenses" />
    )
  ) : null;

  return (
    <MoreActionsDropdown
      menu={
        <Menu>
          <Menu.Header>
            <FormattedMessage id="more_actions" />
          </Menu.Header>
          <Menu.Item onClick={edit}>
            <FormattedMessage id="view_detail" />
          </Menu.Item>
          {isInvited && !isSameUser ? (
            <Menu.Item onClick={resendInvite}>
              <FormattedMessage id="resend" />
            </Menu.Item>
          ) : null}
          {!isSameUser ? (
            <Menu.Item
              danger={isActive}
              onClick={changeStatus}
              disabled={disableDeactivate || disableReactivate}
            >
              <Tooltip title={disableReason}>
                <span>
                  {isActive ? (
                    <FormattedMessage id="deactivate" />
                  ) : (
                    <FormattedMessage id="reactivate" />
                  )}
                </span>
              </Tooltip>
            </Menu.Item>
          ) : null}
        </Menu>
      }
    />
  );
};
